/**
 * Specialisation 5017 — KUK programme-specialisation cards.
 *
 * A row of cards offering the specialisations of a parent programme: thumbnail,
 * specialisation name, blurb, duration and a "Know more" / "Brochure" pair.
 * Ported from kuk-campus-portal's `components/Degree/Specialisations.tsx`; see
 * `Specialisation5017.tsx` for what v1 duplicates and what was dropped.
 *
 * FOUR COLUMNS ARE NEW on `specialisation` — `background_image`,
 * `alt_background_image`, `secondary_cta_text` and `secondary_cta_text_color`.
 * The table had only `background_color` (a flat colour cannot stand in for the
 * reference's band art) and a single `cta_*` set (each card has two actions).
 * See the 20260902100000 migration.
 */

export const SPECIALISATION_5017_CONSTANTS = {
  METADATA_TITLES: {
    metadata: "Specialisations",
  },

  DEFAULT_ROW: {
    id: "1",
    meta_image: "",
    meta_alt_image: "",
    meta_title: "",
    meta_description: "",
    meta_sub_title: "",
    meta_link: "",
    meta_priority: 1,
    meta_status: true,
  },

  SPECIALISATION_FIELDS_CONFIG: [
    {
      fieldName: "title",
      label: "Heading",
      value: "{Online MA} Specialisations",
      valueType: "string",
      filedType: "INPUT",
      placeholder: "{Online MA} Specialisations",
      description:
        "Text inside { } is tinted with the accent colour, matching the reference's coloured programme abbreviation.",
      required: false,
      defaultValue: "{Online MA} Specialisations",
    },
    {
      fieldName: "title_text_color",
      label: "Heading colour",
      value: "#031F44",
      valueType: "string",
      filedType: "COLOR",
      placeholder: "#031F44",
      required: false,
      defaultValue: "#031F44",
    },
    {
      fieldName: "cta_text_color",
      label: "Accent colour",
      value: "#9F1C33",
      valueType: "string",
      filedType: "COLOR",
      placeholder: "#9F1C33",
      description:
        "Tints the braced run in the heading and each card's specialisation name.",
      required: false,
      defaultValue: "#9F1C33",
    },
    {
      fieldName: "background_color",
      label: "Band background",
      value: "#F5F5F5",
      valueType: "string",
      filedType: "COLOR",
      placeholder: "#F5F5F5",
      description: "Painted under the background image, and used alone if none is set.",
      required: false,
      defaultValue: "#F5F5F5",
    },
    {
      fieldName: "background_image",
      label: "Band background image",
      value: "",
      valueType: "string",
      filedType: "FILE",
      placeholder: "Upload the band artwork",
      required: false,
      defaultValue: "",
    },
    {
      fieldName: "alt_background_image",
      label: "Background image alt text",
      value: "",
      valueType: "string",
      filedType: "INPUT",
      placeholder: "Describe the artwork",
      required: false,
      defaultValue: "",
    },
    {
      fieldName: "secondary_cta_text",
      label: "Secondary button",
      value: "Know more",
      valueType: "string",
      filedType: "INPUT",
      placeholder: "Know more",
      description:
        "Opens the lead form unless the row sets its own link.",
      required: false,
      defaultValue: "Know more",
    },
    {
      fieldName: "secondary_cta_text_color",
      label: "Secondary button colour",
      value: "#031F44",
      valueType: "string",
      filedType: "COLOR",
      placeholder: "#031F44",
      description: "Used for both the label and its outline.",
      required: false,
      defaultValue: "#031F44",
    },
    {
      fieldName: "cta_text",
      label: "Primary button",
      value: "Brochure",
      valueType: "string",
      filedType: "INPUT",
      placeholder: "Brochure",
      required: false,
      defaultValue: "Brochure",
    },
    {
      fieldName: "cta_background_color",
      label: "Primary button colour",
      value: "#9F1C33",
      valueType: "string",
      filedType: "COLOR",
      placeholder: "#9F1C33",
      required: false,
      defaultValue: "#9F1C33",
    },
    {
      fieldName: "cta_icon",
      label: "Primary button icon",
      value: "",
      valueType: "string",
      filedType: "FILE",
      placeholder: "Upload an icon, or type an icon name",
      description: "Optional. Sits before the label, as the reference's download glyph does.",
      required: false,
      defaultValue: "",
    },
    {
      fieldName: "cta_icon_alt",
      label: "Primary button icon alt text",
      value: "",
      valueType: "string",
      filedType: "INPUT",
      placeholder: "Describe the icon",
      required: false,
      defaultValue: "",
    },
  ],

  META_FIELDS_CONFIG: [
    {
      fieldName: "meta_image",
      label: "Card image",
      value: "",
      valueType: "string",
      filedType: "FILE",
      placeholder: "Upload the card image",
      required: false,
      defaultValue: "",
    },
    {
      fieldName: "meta_alt_image",
      label: "Card image alt text",
      value: "",
      valueType: "string",
      filedType: "INPUT",
      placeholder: "Describe the image",
      required: false,
      defaultValue: "",
    },
    {
      fieldName: "meta_title",
      label: "Specialisation",
      value: "",
      valueType: "string",
      filedType: "INPUT",
      placeholder: "e.g. Master of Arts in English",
      required: true,
      defaultValue: "",
    },
    {
      fieldName: "meta_description",
      label: "Blurb",
      value: "",
      valueType: "string",
      filedType: "TEXT_AREA",
      rows: 3,
      placeholder: "One or two lines about the specialisation",
      required: false,
      defaultValue: "",
    },
    {
      fieldName: "meta_sub_title",
      label: "Duration",
      value: "",
      valueType: "string",
      filedType: "INPUT",
      placeholder: "e.g. 2 years",
      description: "Shown as 'Duration: …'. Left out of the card when empty.",
      required: false,
      defaultValue: "",
    },
    {
      fieldName: "meta_link",
      label: "Secondary button link (optional)",
      value: "",
      valueType: "string",
      filedType: "INPUT",
      placeholder: "/online-ma-lp/english-lp",
      description:
        "Leave empty on a landing page so the button opens the lead form, which is what the reference does there.",
      required: false,
      defaultValue: "",
    },
    {
      fieldName: "meta_priority",
      label: "Order",
      value: 1,
      valueType: "number",
      filedType: "INPUT",
      placeholder: "1",
      required: false,
      defaultValue: 1,
    },
    {
      fieldName: "meta_status",
      label: "Show this card",
      value: true,
      valueType: "boolean",
      filedType: "SWITCH",
      required: false,
      defaultValue: true,
    },
  ],
};
